
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  MapPin,
  Heart,
  Share2,
  Ticket,
  PlusCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Link } from "react-router-dom";

type SavedEventData = {
  id: string;
  event: {
    id: string;
    name: string;
    start_time: string;
    venue_id: string;
    ticket_price: number;
    image_url: string | null;
    venue: {
      name: string;
      address: string;
    };
  };
  tags?: string[];
};

export const SavedPage = () => {
  const { data: savedEvents, isLoading, error, refetch } = useQuery({
    queryKey: ["savedEvents"],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      
      if (!user.user) {
        throw new Error("Not authenticated");
      }
      
      const { data, error } = await supabase
        .from('saved_events')
        .select(`
          id,
          event:event_id (
            id,
            name,
            start_time,
            venue_id,
            ticket_price,
            image_url,
            venue:venue_id (
              name,
              address
            )
          )
        `)
        .eq('user_id', user.user.id);
      
      if (error) {
        throw error;
      }
      
      return data as SavedEventData[];
    },
  });

  const handleUnsaveEvent = async (eventId: string) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      
      if (!user.user) {
        toast.error("You must be logged in to unsave events");
        return;
      }

      const { error } = await supabase
        .from('saved_events')
        .delete()
        .match({ 
          user_id: user.user.id,
          event_id: eventId
        });

      if (error) {
        throw error;
      }

      refetch();
      toast.success("Event removed from saved list");
    } catch (error: any) {
      toast.error(`Failed to remove event: ${error.message}`);
    }
  };

  const handleShare = async (event: SavedEventData['event']) => {
    try {
      const shareData = {
        title: event.name,
        text: `Check out ${event.name} at ${event.venue.name}!`,
        url: `${window.location.origin}/events/${event.id}`
      };

      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(
          `${shareData.text} ${shareData.url}`
        );
        toast.success("Event details copied to clipboard!");
      }
    } catch (error: any) {
      toast.error("Failed to share event");
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12">Loading saved events...</div>;
  }

  if (error) {
    return <div className="text-red-500 py-8">Failed to load saved events: {(error as Error).message}</div>;
  }

  if (!savedEvents || savedEvents.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Saved Events</h1>
        
        <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed border-gray-200 rounded-lg">
          <Heart className="h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-800 mb-2">No saved events yet</h3>
          <p className="text-gray-500 text-center max-w-md mb-6">
            Browse upcoming events and save the ones you're interested in to keep track of them here.
          </p>
          <Button asChild>
            <Link to="/dashboard/events">
              <PlusCircle className="h-4 w-4 mr-2" />
              Browse Events
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Saved Events</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {savedEvents.map((savedEvent) => {
          const event = savedEvent.event;
          const eventDate = new Date(event.start_time).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
          const eventTime = new Date(event.start_time).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit'
          });
          
          return (
            <Card key={savedEvent.id} className="overflow-hidden">
              <div 
                className="h-48 w-full relative bg-cover bg-center"
                style={{ backgroundImage: `url(${event.image_url || '/placeholder.svg'})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="absolute top-2 right-2 text-white hover:text-red-500"
                  onClick={() => handleUnsaveEvent(event.id)}
                >
                  <Heart className="h-5 w-5 fill-current" />
                </Button>
              </div>
              
              <div className="p-4 space-y-4">
                <h3 className="font-semibold text-lg">{event.name}</h3>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {eventDate} at {eventTime}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    {event.venue.name}, {event.venue.address}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {savedEvent.tags?.map((tag, index) => (
                    <Badge key={index} variant="secondary">
                      {tag}
                    </Badge>
                  )) || (
                    <Badge variant="secondary">Event</Badge>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="font-medium">${event.ticket_price.toFixed(2)}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleShare(event)}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button variant="default" size="sm" asChild>
                      <Link to={`/dashboard/events/${event.id}`}>
                        <Ticket className="h-4 w-4 mr-2" />
                        Get Tickets
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
